import React, { useEffect, useState } from 'react';

import { IconButton } from '@material-ui/core';
import { Skeleton } from '@material-ui/lab';
import { openGenericDrawer } from 'actions/genericDrawer';
import TagIcon from 'components/ObjectiveTagList/TagIcon';
import { TimePeriodDropDown } from 'components/ObjectiveTagList/TagsList';
import { SkeletonContainer } from 'components/ObjectiveTagList/styles';
import useOrganisationSettings from 'components/hooks/useOrganisationSettings';
import { useDispatch } from 'react-redux';
import styled from 'styled-components';
import {
  Divider1,
  FlexContainer,
  SpaceBetweenContainer,
  VerticalFlexContainer,
  StyledTooltip,
  StyledIconButton,
} from 'styles/common';

import LostPerson from 'assets/images/illustrations/lostPerson.png';

import { StringModifier, modifyString } from 'constants/copyHelpers';
import { BackArrowIcon2, CheckIcon2, ChevronRightIcon } from 'constants/icons';
import { sendClickedOnTimePeriodEvent } from 'constants/mixpanelHelper';
import theme from 'constants/theme';

import { getTimeBoundObjectives, getTimeBoundTimePeriods } from 'services/admin';
import { showErrorSnackbar } from 'services/snackbar';

import { Typography4, Typography7, Typography8, Typography9 } from 'uiToolkit/Typography';

const ModalCheckIcon = styled.div`
  &:hover {
    svg {
      fill: ${(props) => props.theme.palette.primary.main};
    }
    button span span {
      border-color: ${(props) => props.theme.palette.primary.main};
    }
  }
`;

const ObjectiveWrapper = styled.div`
  padding: 16px 24px 14px 24px;
  display: flex;
  &:hover {
    background-color: ${(props) => props.theme.palette.background.lightBlue};
    cursor: pointer;
  }
`;

const ObjectiveListWrapper = styled.div`
  flex-grow: 1;
  height: 100%;
  overflow-y: auto;
`;

const HeaderWrapper = styled.div`
  padding: 32px 24px 24px 24px;
`;

const StyledImage = styled.img`
  width: 80%;
  position: relative;
`;

const Loader = () => (
  <SkeletonContainer>
    <Skeleton width="60%" />
    <Skeleton width="40%" />
    <Skeleton width="50%" />
  </SkeletonContainer>
);

const TagsList = ({ tags, onSelect, drawerProps }) => {
  const { drawerLevel } = drawerProps;
  const { moduleName, objectivesName, keyResultsName } = useOrganisationSettings();

  const dispatch = useDispatch();
  // in case of outlook/gmail app
  const openSubObjectiveDrawer = (clickedTag) => {
    dispatch(
      openGenericDrawer({
        contentComponent: (
          <ObjectiveSelectionDrawerComp {...{ ...drawerProps, drawerLevel: drawerLevel + 1, parentTag: clickedTag }} />
        ),
        level: drawerLevel + 1,
      })
    );
  };
  const handleSelectTag = (e, tag) => {
    e.stopPropagation();
    onSelect(tag);
  };
  return tags.map((tag) => (
    <>
      <ObjectiveWrapper onClick={() => openSubObjectiveDrawer(tag)} key={tag.objectiveId}>
        <ModalCheckIcon>
          <StyledIconButton onClick={(e) => handleSelectTag(e, tag)}>
            <StyledTooltip title={`Select this ${modifyString(objectivesName, StringModifier.SENTENCE_CASE)}`}>
              <CheckIcon2
                circularOutline
                containerSize="fixed24px"
                size="fixed10px"
                color="grey"
                bgColor="white"
                hoverShadow
              />
            </StyledTooltip>
          </StyledIconButton>
        </ModalCheckIcon>
        <div style={{ margin: ' 0 16px' }}>
          <TagIcon tagId={tag.objectiveLevel ? tag.objectiveLevel : undefined} />
        </div>
        <VerticalFlexContainer style={{ marginTop: '-4px', flexGrow: '1', minWidth: 0 }}>
          <StyledTooltip title={tag.displayName}>
            <div>
              <Typography4 truncate>{tag.displayName}</Typography4>
            </div>
          </StyledTooltip>
          <StyledTooltip title={tag.description}>
            <div>
              <Typography8 topGutter={0.5} truncate>
                {tag.description}
              </Typography8>
            </div>
          </StyledTooltip>
        </VerticalFlexContainer>
        {drawerLevel < 5 && <ChevronRightIcon color={theme.palette.icons.dark} size="fixed10px" leftGutter={1} />}
      </ObjectiveWrapper>
      <Divider1 />
    </>
  ));
};

const ParentTagNameLevel = ({ tag }) => (
  <FlexContainer style={{ alignItems: 'center' }}>
    <div style={{ marginRight: '16px', marginTop: '5px' }}>
      <TagIcon tagId={tag.objectiveLevel ? tag.objectiveLevel : undefined} />
    </div>
    <Typography4 topGutter={3} bottomGutter={3}>
      {tag.displayName}
    </Typography4>
  </FlexContainer>
);

const NoObjectivesView = ({ drawerLevel }) => (
  <VerticalFlexContainer style={{ alignItems: 'center', justifyContent: 'flex-start', marginTop: '100px' }}>
    <StyledImage src={LostPerson} />
    <Typography9 topGutter={3} bottomGutter={4} customColor={theme.palette.text.dark1} align="center">
      {drawerLevel === 1
        ? 'No objective found for this timeline.'
        : 'No sub-objectives exist for the selected objective.'}
    </Typography9>
  </VerticalFlexContainer>
);

export default function ObjectiveSelectionDrawerComp(props) {
  const {
    onSelect,
    onClose,
    drawerLevel = 1,
    // all three values will be null for first level
    timePeriodData = [],
    topLevelSelectedTimeBoundPeriod = null,
    parentTag = null,
  } = props;
  const [timePeriodsList, setTimePeriodsList] = useState(timePeriodData);
  const [selectedTimeBoundPeriod, setSelectedTimeBoundPeriod] = useState(topLevelSelectedTimeBoundPeriod);
  const [loading, setLoading] = useState(false);
  const [allTags, setAllTags] = useState([]);
  const dispatch = useDispatch();
  const subLevelDrawerProps =
    drawerLevel !== 1
      ? props
      : {
          ...props,
          timePeriodData: timePeriodsList,
          topLevelSelectedTimeBoundPeriod: selectedTimeBoundPeriod,
        };

  const getObjectiveData = ({ timeBound, parentTag = null }) => {
    setLoading(true);
    getTimeBoundObjectives({
      timePeriodObj: timeBound,
      ...(parentTag && { parentId: parentTag?.id }),
    })
      .then((objectives) => {
        setAllTags(
          objectives.map((tag) => ({
            ...tag,
            isEditing: false,
            hasChildTags: true,
          }))
        );
      })
      .catch((err) => dispatch(showErrorSnackbar(err.message)))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    // only runs for first level of drawer as only on first level timePeriodData will be null

    if (timePeriodData.length === 0) {
      getTimePeriodsData();
      // as this also fetches objective data, return
      return;
    }
    // runs once for each drawer level as we need to fetch objective for that level
    getObjectiveData({ timeBound: selectedTimeBoundPeriod, parentTag });
  }, [selectedTimeBoundPeriod]);

  const getTimePeriodsData = () => {
    getTimeBoundTimePeriods()
      .then(({ currentTimeBound, timeBoundDetailsList }) => {
        !selectedTimeBoundPeriod && setSelectedTimeBoundPeriod(currentTimeBound);
        setTimePeriodsList(timeBoundDetailsList);
        return selectedTimeBoundPeriod || currentTimeBound;
      })
      .then((currentTimeBound) => {
        getObjectiveData({ timeBound: currentTimeBound });
      })
      .catch((err) => dispatch(showErrorSnackbar(err.message)));
  };

  if (loading || timePeriodsList.length === 0) {
    return <Loader />;
  }

  return (
    <VerticalFlexContainer>
      <HeaderWrapper>
        <IconButton onClick={() => onClose(drawerLevel)} style={{ marginLeft: '-12px', marginTop: '-12px' }}>
          <BackArrowIcon2 size="fixed16px" />
        </IconButton>
        {parentTag ? (
          <ParentTagNameLevel tag={parentTag} />
        ) : (
          <Typography7 weight="600" topGutter={3} bottomGutter={3}>
            Select Objective
          </Typography7>
        )}
        <SpaceBetweenContainer style={{ alignItems: 'center' }}>
          <Typography4>Timeline</Typography4>
          <div style={{ flexGrow: 1, marginLeft: '24px' }}>
            <TimePeriodDropDown
              timePeriodsList={timePeriodsList}
              selectedTimeBoundPeriod={selectedTimeBoundPeriod}
              setSelectedTimeBoundPeriod={(timePeriod) => {
                sendClickedOnTimePeriodEvent({
                  eventProperties: {
                    source: 'Gmail',
                  },
                });
                setSelectedTimeBoundPeriod(timePeriod);
              }}
              width="100%"
              disabled={drawerLevel !== 1}
            />
          </div>
        </SpaceBetweenContainer>
      </HeaderWrapper>
      <Divider1 />
      {allTags.length === 0 ? (
        <NoObjectivesView drawerLevel={drawerLevel} />
      ) : (
        <ObjectiveListWrapper>
          <TagsList tags={allTags} onSelect={onSelect} drawerProps={subLevelDrawerProps} />
        </ObjectiveListWrapper>
      )}
    </VerticalFlexContainer>
  );
}
